import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { LOGIN_RETURN_TO_KEY, useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/components/layout/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
      },
      {
        path: 'patrimonio',
        name: 'patrimonio',
        component: () => import('@/views/PatrimonioView.vue'),
      },
      {
        path: 'patrimonio/:id',
        name: 'bien-detalle',
        component: () => import('@/views/BienDetalleView.vue'),
      },
      {
        path: 'mapa',
        name: 'mapa',
        component: () => import('@/views/MapaView.vue'),
      },
      {
        path: 'aprovechamientos',
        name: 'aprovechamientos',
        component: () => import('@/views/AprovechamientoView.vue'),
      },
      {
        path: 'documentos',
        name: 'documentos',
        component: () => import('@/views/DocumentoView.vue'),
      },
      {
        path: 'tareas',
        name: 'tareas',
        component: () => import('@/views/TareasView.vue'),
      },
      {
        path: 'expedientes',
        name: 'expedientes',
        component: () => import('@/views/ExpedientesView.vue'),
      },
      {
        path: 'expedientes/:id',
        name: 'expediente-detalle',
        component: () => import('@/views/ExpedienteDetalleView.vue'),
        props: true,
      },
      {
        path: 'miembros',
        name: 'miembros',
        component: () => import('@/views/MiembrosView.vue'),
        meta: { roles: ['PRESIDENTE', 'ADMIN'] },
      },
    ],
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

const OAUTH_RESPONSE_PARAMS = new Set([
  'code', 'state', 'session_state', 'iss', 'error', 'error_description', 'error_uri',
])

function hasKeycloakCallback(hash: string, query: Record<string, unknown>): boolean {
  if (query['code'] !== undefined || query['error'] !== undefined) return true
  if (!hash.startsWith('#')) return false
  const params = new URLSearchParams(hash.slice(1))
  return params.has('code') || params.has('error')
}

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  const isCallback = hasKeycloakCallback(to.hash, to.query)

  // Keycloak must consume the OAuth fragment before the router can replace its URL.
  if (isCallback) {
    try {
      await auth.init()
    } catch {
      console.warn('Keycloak no pudo completar la respuesta de autenticación.')
    }

    if (auth.isAuthenticated) {
      const query = Object.fromEntries(
        Object.entries(to.query).filter(([key]) => !OAUTH_RESPONSE_PARAMS.has(key)),
      )
      return { path: to.path, query, hash: '' }
    }

    if (to.meta.requiresAuth) {
      window.sessionStorage.setItem(LOGIN_RETURN_TO_KEY, to.path)
    }
    // Remove OAuth parameters from the address bar even when Keycloak rejects the callback.
    return { name: 'login' }
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    window.sessionStorage.setItem(LOGIN_RETURN_TO_KEY, isCallback ? to.path : to.fullPath)
    return { name: 'login' }
  }

  if (to.meta.roles && Array.isArray(to.meta.roles)) {
    const allowed = (to.meta.roles as string[]).some((r) => auth.hasRole(r))
    if (!allowed) return { name: 'dashboard' }
  }
})

export default router
